import { Trans, useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { AuthModalManager } from '../components/modals/AuthModalManager';
import { PasswordValidator, validatePassword, type PasswordValidation } from '../components/ui/PasswordValidator';
import type { InvitationDetails } from '../types/api';

/**
 * Bewust buiten AcceptInvitation gedefinieerd.
 *
 * Stond dit binnen het component, dan kreeg het bij elke render een nieuwe
 * functie-identiteit. React ziet dat als een ander componenttype en bouwt de hele
 * subtree opnieuw op, waardoor het invoerveld werd vervangen en de focus na elke
 * toetsaanslag wegviel - je kon dan maar één letter per klik typen.
 */
const Kader: React.FC<{
  children: React.ReactNode;
  showAuthModal: boolean;
  onCloseAuthModal: () => void;
}> = ({ children, showAuthModal, onCloseAuthModal }) => (
  <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
    <div className="sm:mx-auto sm:w-full sm:max-w-md">
      <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">{children}</div>
    </div>
    <AuthModalManager isOpen={showAuthModal} onClose={onCloseAuthModal} initialModal="login" />
  </div>
);

/**
 * Uitnodiging accepteren.
 *
 * Een genodigde zonder account kiest hier direct naam en wachtwoord. Voorheen kreeg hij
 * de instructie om via "wachtwoord vergeten" een tweede e-mail aan te vragen, omdat er
 * bij het uitnodigen al een placeholder-account met een onbekend wachtwoord was gemaakt.
 * Dat kostte drie extra stappen op het moment dat iemand net binnenkomt.
 */
const AcceptInvitation: React.FC = () => {
  const { t } = useTranslation(['auth', 'common']);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();

  const [invitationToken, setInvitationToken] = useState<string | null>(null);
  const [details, setDetails] = useState<InvitationDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(true);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordValidation, setShowPasswordValidation] = useState(false);

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  // De server heeft bij het accepteren een nieuwe sessie meegegeven; de app moet dan
  // opnieuw geladen worden voordat het dashboard de juiste gebruiker toont.
  const [nieuweSessie, setNieuweSessie] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const passwordValidation: PasswordValidation = validatePassword(password);
  const passwordsMatch = password === confirmPassword && password.length > 0;

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setError(t('invitation.noToken'));
      setDetailsLoading(false);
      return;
    }
    setInvitationToken(token);

    apiService
      .getInvitationDetails(token)
      .then(setDetails)
      .catch(() => {
        setError(
          t('invitation.invalidOrExpired')
        );
      })
      .finally(() => setDetailsLoading(false));
  }, [searchParams, t]);

  const handleAcceptInvitation = useCallback(
    async (metGegevens?: { firstName: string; lastName: string; password: string }) => {
      if (!invitationToken) return;

      setLoading(true);
      setError(null);

      try {
        const resultaat = await apiService.acceptInvitation({
          invitationToken,
          ...(metGegevens || {}),
        });
        setSuccess(true);
        setNieuweSessie(resultaat.loggedIn);
        setTimeout(() => {
          if (resultaat.loggedIn) {
            // De server gaf een nieuwe sessie mee. Volledig herladen, anders blijft
            // AuthContext hangen op de gebruiker die er vóór het accepteren was - in
            // dezelfde browser vaak de coach die de uitnodiging verstuurde.
            window.location.assign('/coach');
          } else {
            navigate('/coach', { replace: true });
          }
        }, 3000);
      } catch (err: any) {
        const melding: string = err?.message || '';
        if (melding.includes('Invalid or expired') || melding.includes('niet geldig of verlopen')) {
          setError(t('invitation.invalidOrExpiredShort'));
        } else if (melding.includes('User account not found')) {
          setError(t('invitation.noAccount'));
        } else {
          setError(t('invitation.acceptFailed'));
        }
      } finally {
        setLoading(false);
      }
    },
    [invitationToken, navigate, t]
  );

  // Bestaande gebruiker die net heeft ingelogd: uitnodiging alsnog afronden.
  useEffect(() => {
    const pendingToken = sessionStorage.getItem('pendingInvitationToken');
    if (isAuthenticated && pendingToken && !loading && !success) {
      sessionStorage.removeItem('pendingInvitationToken');
      setInvitationToken(pendingToken);
      handleAcceptInvitation();
    }
  }, [isAuthenticated, loading, success, handleAcceptInvitation]);

  // Vaste props voor de omhulling hieronder; Kader zelf staat op moduleniveau.
  const kaderProps = {
    showAuthModal,
    onCloseAuthModal: () => setShowAuthModal(false),
  };

  if (detailsLoading) {
    return (
      <Kader {...kaderProps}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-hockey-blue mx-auto mb-4" />
          <p className="text-sm text-gray-600">{t('invitation.loading')}</p>
        </div>
      </Kader>
    );
  }

  if (success) {
    return (
      <Kader {...kaderProps}>
        <div className="text-center">
          <CheckCircleIcon className="mx-auto h-12 w-12 text-green-400" />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">{t('invitation.welcomeTitle')}</h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('invitation.welcomeBody')}
          </p>
          <button
            onClick={() => (nieuweSessie ? window.location.assign('/coach') : navigate('/coach'))}
            className="mt-6 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-hockey-blue hover:bg-opacity-90"
          >
            {t('invitation.toDashboard')}
          </button>
        </div>
      </Kader>
    );
  }

  if (!invitationToken || !details) {
    return (
      <Kader {...kaderProps}>
        <div className="text-center">
          <XCircleIcon className="mx-auto h-12 w-12 text-red-400" />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">{t('invitation.invalidTitle')}</h2>
          <p className="mt-2 text-sm text-gray-600">{error}</p>
          <button
            onClick={() => navigate('/coach')}
            className="mt-6 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-hockey-blue hover:bg-opacity-90"
          >
            {t('invitation.toHome')}
          </button>
        </div>
      </Kader>
    );
  }

  // Vaak opent de coach de uitnodigingslink in dezelfde browser waarin hij zelf is
  // ingelogd. Het accepteren gaat gewoon door - het lidmaatschap hoort bij het
  // uitgenodigde adres - maar de gebruiker moet wel weten dat hij van account wisselt.
  const andersIngelogd =
    isAuthenticated &&
    !!user?.email &&
    user.email.toLowerCase() !== details.email.toLowerCase();

  const foutmelding = error && (
    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-md text-left">
      <p className="text-sm text-red-700">{error}</p>
    </div>
  );

  // <Trans> in plaats van interpolatie: de teamnaam staat midden in de zin en heeft
  // eigen opmaak, en de woordvolgorde verschilt per taal.
  const uitnodigingsregel = (
    <p className="mt-2 text-sm text-gray-600">
      <Trans
        i18nKey="auth:invitation.invitedFor"
        values={{
          team: details.teamName || t('invitation.aTeam'),
          role: t(`invitation.roles.${details.role}`, { defaultValue: t('invitation.roles.player') }),
        }}
        components={{ team: <span className="font-medium text-hockey-brown" /> }}
      />
      <br />
      <span className="text-gray-500">{details.email}</span>
    </p>
  );

  // Nieuwe gebruiker: account in één stap activeren.
  if (details.isNewUser) {
    const kanVerzenden =
      !loading && !!firstName.trim() && !!lastName.trim() && passwordValidation.isValid && passwordsMatch;

    return (
      <Kader {...kaderProps}>
        <div className="text-center">
          <UserPlusIcon className="mx-auto h-12 w-12 text-hockey-blue" />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">{t('invitation.completeAccountTitle')}</h2>
          {uitnodigingsregel}
        </div>

        {andersIngelogd && (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-md text-left">
            <p className="text-sm text-amber-800">
              {t('invitation.switchingAccount', { current: user?.email, invited: details.email })}
            </p>
          </div>
        )}

        {foutmelding}

        <div className="mt-6 space-y-4 text-left">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="voornaam" className="block text-sm font-medium text-gray-700 mb-1">
                {t('fields.firstName')}
              </label>
              <input
                id="voornaam"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-hockey-blue focus:outline-none focus:ring-1 focus:ring-hockey-blue"
              />
            </div>
            <div>
              <label htmlFor="achternaam" className="block text-sm font-medium text-gray-700 mb-1">
                {t('fields.lastName')}
              </label>
              <input
                id="achternaam"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-hockey-blue focus:outline-none focus:ring-1 focus:ring-hockey-blue"
              />
            </div>
          </div>

          <div>
            <label htmlFor="wachtwoord" className="block text-sm font-medium text-gray-700 mb-1">
              {t('invitation.choosePassword')}
            </label>
            <input
              id="wachtwoord"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setShowPasswordValidation(true)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-hockey-blue focus:outline-none focus:ring-1 focus:ring-hockey-blue"
            />
            <PasswordValidator
              password={password}
              showValidation={showPasswordValidation}
              validation={passwordValidation}
            />
          </div>

          <div>
            <label htmlFor="herhaal" className="block text-sm font-medium text-gray-700 mb-1">
              {t('invitation.repeatPassword')}
            </label>
            <input
              id="herhaal"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-hockey-blue focus:outline-none focus:ring-1 focus:ring-hockey-blue"
            />
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p className="mt-1 text-sm text-red-600">{t('invitation.passwordsMismatch')}</p>
            )}
          </div>

          <button
            onClick={() =>
              handleAcceptInvitation({
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                password,
              })
            }
            disabled={!kanVerzenden}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-hockey-blue hover:bg-opacity-90 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? t('invitation.working') : t('invitation.createAndJoin')}
          </button>
        </div>
      </Kader>
    );
  }

  // Bestaande gebruiker die nog niet is ingelogd.
  if (!isAuthenticated) {
    return (
      <Kader {...kaderProps}>
        <div className="text-center">
          <UserPlusIcon className="mx-auto h-12 w-12 text-hockey-blue" />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">{t('invitation.acceptTitle')}</h2>
          {uitnodigingsregel}
          <p className="mt-4 text-sm text-gray-600">
            {t('invitation.existingAccount')}
          </p>
        </div>

        {foutmelding}

        <button
          onClick={() => {
            sessionStorage.setItem('pendingInvitationToken', invitationToken);
            setShowAuthModal(true);
          }}
          className="mt-6 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-hockey-blue hover:bg-opacity-90"
        >
          {t('login.submit')}
        </button>
      </Kader>
    );
  }

  // Bestaande gebruiker, maar er staat een sessie van iemand anders open. Accepteren zou
  // hier lukken - het lidmaatschap gaat naar het uitgenodigde adres - maar daarna zou het
  // dashboard nog steeds dat van de ander tonen. Eerst wisselen dus.
  if (andersIngelogd) {
    return (
      <Kader {...kaderProps}>
        <div className="text-center">
          <UserPlusIcon className="mx-auto h-12 w-12 text-hockey-blue" />
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900">{t('invitation.acceptTitle')}</h2>
          {uitnodigingsregel}
          <p className="mt-4 text-sm text-gray-600">
            {t('invitation.loggedInAsOther', { current: user?.email, invited: details.email })}
          </p>
        </div>

        {foutmelding}

        <button
          onClick={() => {
            sessionStorage.setItem('pendingInvitationToken', invitationToken);
            logout();
            setShowAuthModal(true);
          }}
          className="mt-6 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-hockey-blue hover:bg-opacity-90"
        >
          {t('invitation.switchAccount')}
        </button>
      </Kader>
    );
  }

  // Bestaande gebruiker, al ingelogd.
  return (
    <Kader {...kaderProps}>
      <div className="text-center">
        <UserPlusIcon className="mx-auto h-12 w-12 text-hockey-blue" />
        <h2 className="mt-6 text-3xl font-extrabold text-gray-900">{t('invitation.acceptTitle')}</h2>
        {uitnodigingsregel}
      </div>

      {foutmelding}

      <button
        onClick={() => handleAcceptInvitation()}
        disabled={loading}
        className="mt-6 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-hockey-blue hover:bg-opacity-90 disabled:opacity-50"
      >
        {loading ? t('invitation.working') : t('invitation.accept')}
      </button>

      <button
        onClick={() => navigate('/coach')}
        className="mt-4 w-full text-sm text-gray-500 hover:text-gray-700"
      >
        {t('common:actions.cancel')}
      </button>
    </Kader>
  );
};

export default AcceptInvitation;
